<template>
  <div
    class="c-content-card"
    :class="cardClass"
    :style="cardStyles"
    @click="cardClick"
  >
    <div
      class="c-content-card__content-container l-flex"
      :class="featured ? 'l-flex--justify-start' : ''"
    >
      <div
        class="c-content-card__content"
        :class="contentClass"
      >
        <nuxt-img
          v-if="mobleOrDesktopImg && mobleOrDesktopImg.src"
          class="c-content-card__image"
          :class="imageClass"
          v-bind="$getImgOptions(mobleOrDesktopImg, {
            format: 'webp',
            quality: 60,
            alt: mobleOrDesktopImg.title || 'Featured Image'
          })"
          :style="imageHeightCalc"
          role="presentation"
        />
        <vid
          v-if="video"
          v-bind="video"
          :class="imageClass"
        />

        <div
          v-if="featured && subtitleFont != 'Five Boroughs Handwriting'"
          class="c-content-card__featured-bar u-margin-bottom-x-small"
        />

        <div
          v-if="titleLogo"
          class="u-text-align-left"
        >
          <img
            class="c-content-card--logo"
            :src="titleLogo.src"
            :alt="titleLogo.alt || title + ' logo'"
          >
        </div>
        <!-- eslint-disable vue/no-v-html -->
        <h2
          v-if="titleValue"
          :class="titleClass"
          :style="{ fontFamily: titleFont }"
          v-html="titleValue"
        />
        <h3
          v-if="subtitle"
          :class="subtitleClass"
          :style="{ fontFamily: subtitleFont }"
          v-html="subtitle"
        />
        <!-- eslint-enable vue/no-v-html -->
        <div
          v-if="featured && subtitleFont == 'Five Boroughs Handwriting'"
          class="c-content-card__featured-bar u-margin-bottom-x-small"
        />
        <wysiwyg
          v-if="descriptionValue"
          :class="wysiwygClass"
          :paragraph-size="descriptionTypography"
          :html="descriptionValue"
          :link-color="whiteText ? 'white' : 'regular'"
        />
        <slot />
        <btn
          v-if="cta"
          v-bind="cta"
          class="u-margin-bottom-small"
          :class="{
            'u-screen-reader-only': cardClickable
          }"
          @click="buttonClick"
        >
          <!-- eslint-disable vue/no-v-html -->
          <div
            class="c-content-card__cta-text"
            v-html="cta.text"
          />
          <!-- eslint-enable vue/no-v-html -->
        </btn>
        <wysiwyg
          v-if="belowButtonText"
          :html="belowButtonText"
          :class="belowButtonTextClass"
        />
        <div
          v-if="additionalImages.length > 0"
          class="c-content-card__additional-images"
        >
          <nuxt-img
            v-for="(item, index) in additionalImages"
            :key="`${index}-${item.image.src}`"
            v-bind="$getImgOptions(item.image, {
              alt: item.image.title || 'Additional Image',
              quality: 60,
              format: 'webp'
            })"
            role="presentation"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Btn from '../button/Btn'
import Vid from '../video/Vid'
import Wysiwyg from '../wysiwyg/Wysiwyg'
import isMobile from '@mixins/isMobile';
import { UrlFilters } from '@mixins/cdnMixins'

export default {
  name: 'ContentCard',
  components: {
    Btn,
    Wysiwyg,
    Vid,
  },
  mixins: [
    //this.isMobile is true screen width < 900px
    isMobile,
    UrlFilters,
  ],
  props: {
    backgroundColor: {
      type: String,
      default: '',
    },
    backgroundImage: {
      type: Object,
      default: () => ({
        src: '',
        alt: 'background image',
      })
    },
    textAlignment: {
      type: String,
      default: 'center',
    },
    mobileTextAlignment: {
      type: String,
      default: '',
    },
    image: {
      type: Object,
      default: ()=> ({}),
    },
    mobileImage: {
      type: Object,
      default: ()=> ({}),
    },
    additionalImages: {
      type: Array,
      default: () => ([])
    },
    video: {
      type: Object,
      default: null,
    },
    imageMarginBottom: {
      type: String,
      default: ''
    },
    imageHeight: {
      type: String,
      default: '',
    },
    imageWidth: {
      type: String,
      default: '',
    },
    cta: {
      type: Object,
      default: null
    },
    whiteText: {
      type: Boolean,
      default: false
    },
    subtitle: {
      type: String,
      default: '',
    },
    subtitleTypography: {
      type: String,
      default: 't-heading-5'
    },
    subtitleMarginBottom: {
      type: String,
      default: 'xx-small'
    },
    handwritingTitleMarginBottom: {
      type: String,
      default: 'small'
    },
    handwritingSubtitleMarginBottom: {
      type: String,
      default: 'small'
    },
    subtitleFont: {
      type: String,
      default: 'Poppins'
    },
    subtitleColor: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: '',
    },
    descriptionsTypography: {
      type: String,
      default: ''
    },
    descriptionMarginBottom: {
      type: String,
      default: ''
    },
    descriptionColor: {
      type: String,
      default: '',
    },
    productDescription: {
      type: Boolean,
      default: false,
    },
    productImage: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    titleFont: {
      type: String,
      default: 'Poppins'
    },
    titleMarginBottom: {
      type: String,
      default: 'x-small'
    },
    titleTypography: {
      type: String,
      default: 't-heading-7'
    },
    titleColor: {
      type: String,
      default: '',
    },
    buttonType: {
      type: String,
      default: 'link',
    },
    featured: {
      type: Boolean,
      default: false,
    },
    titleLogo: {
      type: Object,
      default: ()=>{}
    },
    belowButtonText: {
      type: String,
      default: '',
    },
    belowButtonTextColor: {
      type: String,
      default: ''
    },
    belowButtonTextMarginBottom: {
      type: String,
      default: ''
    },
    cardClickable: {
      type: Boolean,
      default: false
    },
    newBadge: {
      type: Boolean,
      default: false
    },
    product: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    mobleOrDesktopImg() {
      let isMobile = this.isMobile
      let mobileImage = this.mobileImage
      let image = this.image
      const product = this.product
      const productImage = this.productImage
      if(productImage && product && product.media) {
        return product.media[0]
      } else if (isMobile && mobileImage && mobileImage.src) {
        return mobileImage
      } else {
        return image
      }
    },
    descriptionTypography() {
      const descriptionTypography = this.descriptionsTypography
      return descriptionTypography
    },
    imageHeightCalc() {
      const productImage = this.productImage
      let imageHeight = this.imageHeight
      if(productImage) {
        imageHeight = '500';
      }
      return imageHeight ? 'height: ' + imageHeight + 'px' : ''
    },
    imageClass() {
      let result = [];
      let imageMarginBottom = this.imageMarginBottom;
      let centerImage = this.centerImage;
      if (imageMarginBottom) {
        result.push("u-margin-bottom-" + imageMarginBottom);
      }
      else if (!this.video) {
        result.push('u-margin-bottom-small');
      }
      if (centerImage) {
        result.push("u-margin-auto");
      }
      return  result;
    },
    cardClass() {
      const isMobile = this.isMobile
      const textAlignment = this.textAlignment
      const mobileTextAlignment = this.mobileTextAlignment
      const cardClickable = this.cardClickable
      const backgroundColor = this.backgroundColor
      let results = []
      if (isMobile) {
        if (mobileTextAlignment) {
          results.push("u-text-align-" + mobileTextAlignment)
        } else {
          results.push("u-text-align-" + textAlignment)
        }
      } else {
        results.push("u-text-align-" + textAlignment)
      }
      if (backgroundColor) {
        results.push('u-background-color-' + backgroundColor)
      }
      if (cardClickable) {
        results.push('c-content-card--clickable')
      }
      return results;
    },
    cardStyles() {
      const backgroundColor = this.backgroundColor
      const backgroundImage = this.backgroundImage
      let result = {}
      if (backgroundColor) {
        result['background-color'] = backgroundColor
      }
      if (backgroundImage) {
        result['background-image'] = `url(${backgroundImage.url})`
      }
      return result
    },
    centerImage(){
      if(!this.textAlignment || this.textAlignment == 'center'){
        return true;
      }
      else{ return false;}

    },
    contentClass(){
      const newBadge = this.newBadge
      const productDescription = this.productDescription
      let result = []
      if(newBadge) {
        result.push('c-content-card--new')
      }
      if(productDescription) {
        result.push('c-content-card__content--product-details')
      }
      return result
    },
    titleValue() {
      const productDescription = this.productDescription
      const title = this.title
      const product = this.product
      let result = title
      if(productDescription && product) {
        result = product.title
      } else {
        result = title
      }
      return result
    },
    descriptionValue() {
      const productDescription = this.productDescription
      const description = this.description
      const product = this.product
      let result = null
      if(description) {
        result = description
      } else if (productDescription && product &&  product.description) {
        result = product.description
      }
      return result
    },
    titleClass() {
      let results = [this.titleTypography]
      if (this.titleFont == 'Five Boroughs Handwriting') {
        results.push('c-content-card__title-boroughs');
        if (this.subtitle || this.description || this.cta) {
          results.push("u-margin-bottom-" + this.handwritingTitleMarginBottom);
        }
      } else {
        if (this.subtitle || this.description || this.cta) {
          results.push("u-margin-bottom-" + this.titleMarginBottom);
        }
      }
      if (this.featured) {
        results.push("c-content-card__title");
      }
      if (this.whiteText) {
        results.push(this.titleTypography + "--white");
      }
      if (this.titleColor) {
        results.push(this.titleTypography + '--' + this.titleColor);
      }
      return results;
    },
    subtitleClass() {
      let results = [this.subtitleTypography];
      if (this.subtitleFont == 'Five Boroughs Handwriting') {
        results.push('c-content-card__subtitle-boroughs');
        if (this.description || this.cta) {
          results.push("u-margin-bottom-" + this.handwritingSubtitleMarginBottom);
        }
      } else {
        if (this.description || this.cta) {
          results.push("u-margin-bottom-" + this.subtitleMarginBottom);
        }
      }
      if (this.featured) {
        results.push('c-content-card__subtitle')
      }
      if (this.whiteText) {
        results.push(this.subtitleTypography + "--white");
      }
      if (this.subtitleColor) {
        results.push(this.subtitleTypography + '--' + this.subtitleColor);
      }
      return results;
    },
    wysiwygClass() {
      let wysiwygClass = [];
      if (this.descriptionMarginBottom.length > 0) {
        wysiwygClass.push("u-margin-bottom-" + this.descriptionMarginBottom);
      }
      if (this.featured) {
        wysiwygClass.push("c-content-card__description");
      }
      if (this.whiteText) {
        wysiwygClass.push("u-foreground-white");
      }
      if (this.descriptionColor) {
        wysiwygClass.push(this.titleTypography + '--' + this.descriptionColor);
      }
      else if (this.titleColor) {
        wysiwygClass.push(this.titleTypography + '--' + this.titleColor);
      }
      return wysiwygClass;
    },
    belowButtonTextClass() {
      let results = ["u-text-align-center t-paragraph"];
      if (this.belowButtonTextColor) {
        results.push("t-paragraph--" + this.belowButtonTextColor);
      }
      if (this.belowButtonTextMarginBottom) {
        results.push("u-margin-bottom-" + this.belowButtonTextMarginBottom);
      }
      return results;
    }
  },
  methods: {
    cardClick() {
      if (this.cardClickable && this.cta) {
        const { url, externalUrl } = this.cta
        if (externalUrl) {
          window.location = url
        } else {
          this.$router.push(url)
        }
      }
    },
    buttonClick(event) {
      this.$emit("click", event);
    }
  }
};
</script>

<style lang="scss" scoped>
  .c-content-card {
    &--clickable {
      cursor: pointer;
      padding: 1px;
      &:hover {
        padding: 0;
        border-width: 1px;
        border-style: solid;
      }
    }
    &__content {
      &--product-details {
        padding: 0 rem(40px);
      }
    }
    &__description {
      max-width: 220px;
      //margin: 0 auto;
    }
    &__image {
      max-width: 100%;
      width: auto;
      display:block;
      object-fit: contain;
      margin: auto;
    }

    &__subtitle {
      max-width: rem(350px);
    }

    &__title-boroughs {
      font-size: rem(85px);
      letter-spacing: rem(0.8px);
      line-height: rem(50px);
      text-transform: lowercase;
      font-weight: $font-weight-book;
      max-width: 100%;
    }

    &__subtitle-boroughs {
      font-size: rem(80px);
      letter-spacing: rem(0.8px);
      line-height: rem(50px);
      text-transform: lowercase;
      font-weight: $font-weight-book;
      max-width: 100%;
    }

    &__description {
      max-width: rem(450px);
    }
    &__featured-bar {
      width: 42px;
      height: 7px;
      @include themify($themes) {
        background-color: themed('accent', 'light')
      }
    }
    &--logo {
      max-height: rem(50px);
    }
    &--new {
      position: relative;
      &::after {
        content: "";
        background-image: url("~@/assets/images/new_badge_blue.png");
        background-repeat: no-repeat;
        background-size: 100%;
        width: rem(47px * 1.6);
        height: rem(20px * 1.6);
        position: absolute;
        right: rem(10px);
        top: rem(10px);
      }
    }
    &__cta-text {
      line-height: 1.5;
    }
    &__additional-images {
      display: flex;
      align-items: center;
      gap: rem(20px);
      img {
        width: auto;
        max-width: 50%;
      }
    }
  }
</style>
